/**
 * Logica di layout dell'editor card (ordine + colonne dei campi prodotto).
 * Unica fonte condivisa da editor card e anteprima del tab "Posizione campi": il default
 * storico vive nel backend (LEGACY_EDITOR_COLUMNS), qui si lavora sui valori effettivi
 * (editorColumn 0 = automatica, 1..3 = colonna fissa; editorPosition null = mai salvato).
 * @module fields/utils/editorLayout
 */

export const EDITOR_COLUMN_COUNT = 3;

// Campi prodotto mostrati sempre nella colonna fissa dell'editor card, sotto il Seriale, per
// ogni cliente: restano fuori dalle colonne dinamiche (editor e anteprima). La loro posizione
// in "Posizione campi" conta ancora per l'ordine dei tag XML, quindi restano nella lista.
export const FIXED_PRODUCT_FIELD_KEYS = new Set(["codice_cliente"]);

function hasFinitePosition(field)
{
	return Number.isFinite(field?.editorPosition);
}

// Il layout globale è attivo solo se è stato salvato almeno una volta: finché nessun campo
// ha editorPosition, ogni cliente deve continuare a vedere l'ordine salvato in /org.
export function hasSavedEditorLayout(fields)
{
	if (!Array.isArray(fields))
		return false;

	return fields.some(hasFinitePosition);
}

// Ordina per editorPosition mantenendo l'ordine di input a parità (il sort di ES2019+ è già
// stabile, l'indice di partenza lo rende esplicito). Senza layout salvato ritorna una copia
// nell'ordine di input invariato.
export function sortByEditorSequence(fields)
{
	if (!Array.isArray(fields))
		return [];

	const list = [...fields];
	if (!hasSavedEditorLayout(list))
		return list;

	return list
		.map((field, idx) => ({field, idx}))
		.sort((a, b) =>
		{
			const posA = hasFinitePosition(a.field) ? a.field.editorPosition : Number.POSITIVE_INFINITY;
			const posB = hasFinitePosition(b.field) ? b.field.editorPosition : Number.POSITIVE_INFINITY;
			if (posA !== posB)
				return posA - posB;
			return a.idx - b.idx;
		})
		.map(({field}) => field);
}

// Distribuisce i campi già ordinati in EDITOR_COLUMN_COUNT colonne: editorColumn 1..3 fissa
// la colonna, altrimenti vince la colonna con meno campi in quel momento (a parità la più a
// sinistra). Ritorna solo i bucket non vuoti, come faceva FlyerProductSection.
export function buildEditorColumns(fields)
{
	const ordered = sortByEditorSequence(fields);
	if (!ordered.length)
		return [];

	const buckets = Array.from({length: EDITOR_COLUMN_COUNT}, () => []);
	const counts = new Array(EDITOR_COLUMN_COUNT).fill(0);

	ordered.forEach((field) =>
	{
		const preferred = field?.editorColumn;
		let index = Number.isInteger(preferred) && preferred >= 1 && preferred <= EDITOR_COLUMN_COUNT
			? preferred - 1
			: counts.indexOf(Math.min(...counts));
		if (!Number.isInteger(index) || index < 0 || index >= EDITOR_COLUMN_COUNT)
			index = 0;

		buckets[index].push(field);
		counts[index] += 1;
	});

	return buckets.filter((bucket) => bucket.length > 0);
}
